import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { $createTextNode, $getNodeByKey } from 'lexical';
import { useState, useEffect, useRef } from 'react';
import { PlaceholderTicketNode } from './PlaceholderTicketNode';

interface PlaceholderTicketComponentProps {
  title: string;
  nodeKey: string;
}

export function PlaceholderTicketComponent({
  title,
  nodeKey,
}: PlaceholderTicketComponentProps) {
  const [editor] = useLexicalComposerContext();
  const isEditable = useLexicalEditable();
  const [isSelected] = useLexicalNodeSelection(nodeKey);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync editValue with title prop when it changes
  useEffect(() => {
    setEditValue(title);
  }, [title]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    if (editValue.trim()) {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node instanceof PlaceholderTicketNode) {
          node.setTitle(editValue.trim());
        }
      });
    }
    setIsEditing(false);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (node) {
        // Replace placeholder with plain text (no #'...' wrapper)
        const textNode = $createTextNode(title);
        node.replace(textNode);
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Stop propagation for all keyboard events to prevent Lexical from capturing them
    e.stopPropagation();

    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditValue(title);
      setIsEditing(false);
    }
    // Let all other keys (including Ctrl+Backspace, Ctrl+Delete, etc.) work normally
  };

  if (!isEditable) {
    // Read-only viewer: nothing will be created from here, so show the
    // placeholder as written (same as the CLI) instead of an editable chip.
    return (
      <span className="text-muted-foreground" title="Placeholder ticket">
        #'{title}'
      </span>
    );
  }

  if (isEditing) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-green-50 px-1 text-green-600">
        <span className="font-medium">#??</span>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="bg-transparent outline-hidden"
          size={editValue.length || 1}
        />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex cursor-pointer items-center gap-1 rounded bg-green-50 px-1
        text-green-600 hover:bg-green-100
        ${isSelected ? 'ring-2 ring-green-500 ring-offset-1' : ''}`}
      onClick={() => setIsEditing(true)}
      title="Click to edit ticket title (will be created on submit)"
    >
      <span className="font-medium">#??</span>
      <span>{title}</span>
      <button
        onClick={handleRemove}
        className="text-muted-foreground hover:bg-muted hover:text-foreground ml-1 rounded px-0.5
          text-xs"
        title="Convert to plain text"
      >
        ×
      </button>
    </span>
  );
}
